import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { Button } from "@/components/ui/button.js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { useToast } from "@/components/ui/toast.js";
import { Badge } from "@/components/ui/badge.js";
import type { SyncConflict } from "@/lib/types.js";
import { ApiError } from "@/lib/api.js";

/**
 * Sync conflict resolution view per REQ-037 and Flow H. GitHub-merge-conflict
 * style: side-by-side diff, accept-external / reject buttons. The handler maps
 * 409 external_change_still_infeasible to a banner so staff can pick a different
 * recovery (cancel local, reschedule local).
 */
export function SyncConflictsPage(): React.JSX.Element {
  const { api } = useTenant();
  const { toast } = useToast();
  const conflicts = useAsync(() => api.listSyncConflicts(), [api]);

  async function resolve(id: string, resolution: "accept" | "reject"): Promise<void> {
    try {
      await api.resolveSyncConflict(id, resolution);
      toast({ title: `Conflict ${resolution}ed`, variant: "success" });
      conflicts.reload();
    } catch (error) {
      if (error instanceof ApiError && error.code === "external_change_still_infeasible") {
        toast({
          title: "Still infeasible",
          description: "Cancel the conflicting local appointment or reschedule, then retry accept.",
          variant: "warning"
        });
      } else {
        toast({
          title: "Could not resolve",
          description: error instanceof Error ? error.message : String(error),
          variant: "destructive"
        });
      }
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-lg font-semibold">Sync conflicts</h1>
      {conflicts.loading ? (
        <Skeleton className="h-48 w-full" />
      ) : (conflicts.data?.data ?? []).length === 0 ? (
        <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
          No open conflicts.
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {(conflicts.data?.data ?? []).map((conflict) => (
            <ConflictCard key={conflict.id} conflict={conflict} onResolve={resolve} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ConflictCard({ conflict, onResolve }: { conflict: SyncConflict; onResolve: (id: string, r: "accept" | "reject") => void }): React.JSX.Element {
  const local = conflict.local_state as Record<string, unknown>;
  const external = conflict.external_state as Record<string, unknown>;
  return (
    <li>
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium">Appointment {String(conflict.appointment_id).slice(0, 8)}…</CardTitle>
          <Badge variant="warning">{conflict.status}</Badge>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Side title="Local" data={local} />
          <Side title="External" data={external} />
          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button variant="outline" onClick={() => onResolve(conflict.id, "reject")}>
              Reject external
            </Button>
            <Button onClick={() => onResolve(conflict.id, "accept")}>Accept external</Button>
          </div>
        </CardContent>
      </Card>
    </li>
  );
}

function Side({ title, data }: { title: string; data: Record<string, unknown> }): React.JSX.Element {
  return (
    <div className="rounded-md border bg-muted/40 p-2">
      <div className="text-xs font-medium uppercase text-muted-foreground">{title}</div>
      <dl className="mt-1 grid grid-cols-[120px_1fr] gap-x-2 gap-y-0.5 text-xs">
        {Object.entries(data).map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="break-all">{typeof v === "object" ? JSON.stringify(v) : String(v)}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
